// ============================================================
// THE FORCE FIELD'S RING (plan body-map T1; the user's own design, ruled
// 2026-10-09). At rest the field shows only as its emitter: a band of
// hexagons fitted edge to edge round the feet, turning slowly, each column
// one cell high and a second coming and going (more often the stronger the
// field; protection rating 10-50). Its far half is behind the figure, its
// near half before it. What an attack does to it:
//
//   a block        the ring flares (the shield itself rises at the struck
//                  place: ./moments)
//   Overloader     the flare strains amber
//   Fieldbreaker   the ring gutters as the first hit lands - grey, dim
//   Scrambler      the ring stutters in static while the attack plays
//   an overload    a beat after the block the ring shatters outward, gone
//
// Pure. Zero React dependency. Frame units; seconds.
// ============================================================

import type { Box } from './frame';
import { firstLanded, type BurstHit } from './burst';
import { hash01 } from './hash';
import { hasSpecial } from './specials';

/** The ring under a figure: its centre and ground line, its radius, its columns, a cell's size, its strength. */
export interface FieldRing {
  cx: number;
  gy: number;
  R: number;
  /** Columns round the ring - even, so the offset rows close the tiling. */
  n: number;
  /** A cell's half-height. */
  s: number;
  /** The field's strength, its protection rating over 50. */
  k: number;
}

/** The ring round a figure with these whole bounds, for a field of this protection rating. */
export function fieldRing([x0, , x1, y1]: Box, rating: number): FieldRing {
  const R = Math.max(420, (x1 - x0) * 0.5);
  const n = 2 * Math.round((Math.PI * R) / 120);
  return { cx: (x0 + x1) / 2, gy: y1 + 20, R, n, s: (2 * Math.PI * R) / n / 1.5, k: rating / 50 };
}

/** What the attack playing does to the ring at a moment: its flare, its guttering, static, strain, its shattering. */
export interface FieldMoment {
  /** 1 at a block, fading over 0.9 s. */
  flash: number;
  /** 1 as a Fieldbreaker hit lands, fading over 0.9 s. */
  gutter: number;
  scrambled: boolean;
  strain: boolean;
  /** Seconds since the overload shattered it (0: whole). */
  shatter: number;
}

/** How long a flare and a guttering fade; when an overload shatters the ring after its block (the bench's beat). */
const FIELD = { fade: 0.9, shatterAfter: 1.03, shatterFor: 1.2 };

const since = (at: number | undefined, e: number) =>
  at === undefined || e < at ? 0 : Math.max(0, 1 - (e - at) / FIELD.fade);

/** The burst's effect on the ring `e` seconds in (null: no attack playing). */
export function fieldMoment(
  play: { hits: readonly BurstHit[]; specials: readonly string[] } | null,
  e: number,
): FieldMoment {
  if (!play) return { flash: 0, gutter: 0, scrambled: false, strain: false, shatter: 0 };
  const block = play.hits.filter((h) => h.blocked && h.at <= e + 1e-9).at(-1);
  const over = play.hits.find((h) => h.overloaded);
  const breaker = hasSpecial(play.specials, 'Fieldbreaker') ? firstLanded(play) : null;
  return {
    flash: since(block?.at, e),
    gutter: since(breaker?.at, e),
    scrambled: hasSpecial(play.specials, 'Scrambler'),
    strain: hasSpecial(play.specials, 'Overloader'),
    shatter: over ? Math.max(0, e - over.at - FIELD.shatterAfter) : 0,
  };
}

/** Seconds an attack holds the ring: until an overload has shattered it (0 with none) - its play must last so long. */
export function fieldHold(hits: readonly BurstHit[]): number {
  const over = hits.find((h) => h.overloaded);
  return over ? over.at + FIELD.shatterAfter + FIELD.shatterFor : 0;
}

/** One cell of the ring at a moment: its place among the columns, its points, how it is drawn. */
export interface RingCell {
  column: number;
  row: 0 | 1;
  /** On the near half (before the figure). */
  front: boolean;
  points: string;
  opacity: number;
  fillOpacity: number;
  /** Which edge it shows: flared, strained, guttered or at rest. */
  edge: 'flash' | 'strain' | 'gutter' | 'rest';
  width: number;
}

/** How present the column's second cell is at `t`: each on its own slow wave, more often with a stronger field. */
function secondCell(j: number, t: number, k: number): number {
  const wave = 0.5 + 0.5 * Math.sin(t * (0.5 + 0.6 * hash01(j, 1)) + 6.283 * hash01(j, 2));
  return Math.min(1, Math.max(0, (wave - (0.78 - 0.45 * k)) * 5));
}

/** A cell's six points on the cylinder at angle `th`, `v0` above the ground, thrown out by a shatter. */
function cellPoints(g: FieldRing, th: number, v0: number, out: number, up: number): string {
  return Array.from({ length: 6 }, (_, q) => {
    const ang = (q * Math.PI) / 3;
    const tv = th + (g.s * 0.96 * Math.cos(ang)) / g.R;
    const rr = g.R + out;
    const y = g.gy + rr * 0.17 * Math.sin(tv) - v0 - g.s * 0.96 * Math.sin(ang) - up;
    return `${Math.round(g.cx + rr * Math.cos(tv))},${Math.round(y)}`;
  }).join(' ');
}

/** The edge a cell shows: flared (strained under Overloader), else guttered, else at rest. */
function edgeOf(m: FieldMoment): RingCell['edge'] {
  if (m.flash > 0.3) return m.strain ? 'strain' : 'flash';
  return m.gutter > 0.3 ? 'gutter' : 'rest';
}

/** How bright a cell is: present, in static or not, near or far, flared, guttered, shattering. */
function cellOpacity(g: FieldRing, m: FieldMoment, present: number, noise: number, front: boolean): number {
  const glow = 0.35 + 0.4 * g.k + 0.5 * m.flash;
  const fade = Math.max(0, 1 - m.shatter / FIELD.shatterFor);
  return Number((present * noise * (front ? 1 : 0.45) * glow * (1 - 0.8 * m.gutter) * fade).toFixed(2));
}

/** In static, a cell flickers between weak and weaker each twelfth of a second. */
const staticOf = (m: FieldMoment, j: number, row: number, t: number) =>
  !m.scrambled ? 1 : hash01(j * 7 + row, Math.floor(t * 12)) < 0.3 ? 0.15 : 0.6;

/** Cell `row` of column `j` at angle `th`: placed on the cylinder (thrown out by a shatter) and lit by the moment. */
function ringCell(g: FieldRing, j: number, row: 0 | 1, th: number, t: number, m: FieldMoment): RingCell {
  const rows = Math.sqrt(3) * g.s;
  const front = Math.sin(th) > 0;
  const present = row ? secondCell(j, t, g.k) : 1;
  const v0 = (row + 0.5) * rows + (j % 2 ? rows / 2 : 0);
  const out = m.shatter * (300 + 500 * hash01(j, row + 3));
  const up = m.shatter * 200 * (hash01(j, row + 5) - 0.3);
  return {
    column: j,
    row,
    front,
    points: cellPoints(g, th, v0, out, up),
    opacity: present < 0.02 ? 0 : cellOpacity(g, m, present, staticOf(m, j, row, t), front),
    fillOpacity: Number((0.05 + 0.3 * m.flash).toFixed(2)),
    edge: edgeOf(m),
    width: Number((4 + 4 * m.flash).toFixed(1)),
  };
}

/** Every cell of the ring at `t` (two a column; an absent second cell has no opacity), as the moment draws them. */
export function ringCells(g: FieldRing, t: number, m: FieldMoment): RingCell[] {
  return Array.from({ length: g.n }, (_, j) => {
    const th = (j / g.n) * Math.PI * 2 + t * 0.12;
    return [ringCell(g, j, 0, th, t, m), ringCell(g, j, 1, th, t, m)];
  }).flat();
}
